import { useState } from "react";
import { Box, Dialog, IconButton, Typography } from "@mui/material";
import { FileUpload } from "components/creation/FileUploader";
import useIsMobile from "components/hooks/useIsMobile";
import CloseIcon from "@mui/icons-material/Close";
import { Paperclip, X, Download, SquareUserRound } from "lucide-react";
import theme from "theme";

const ImageDialog = ({
  imageUrl,
  onClose,
}: {
  imageUrl: string | null;
  onClose: () => void;
}) => {
  // Hooks are always called at the top level of the component, even if no attachment is provided.
  const isMobile = useIsMobile();
  return (
    <Dialog
      open={!!imageUrl}
      onClose={onClose}
      fullScreen={isMobile} // Full screen on mobile
      PaperProps={{
        sx: {
          width: isMobile ? "100%" : "auto",
          height: isMobile ? "100%" : "auto",
          maxWidth: "100%",
          maxHeight: "100%",
          position: "relative",
          borderRadius: isMobile ? 0 : "16px",
          padding: isMobile ? "10vh 10vw" : "35px",
          backgroundColor: "transparent",
          boxShadow: "none",
        },
      }}
    >
      {/* Close Button */}
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: "absolute",
          right: 0,
          top: 0,
          padding: "4px",
          backgroundColor: "#e5e5e5",
          zIndex: 10,
          color: "#000",
          "&:hover": {
            backgroundColor: "#e5e5e5",
          },
          ...(isMobile
            ? {
                top: 24,
                right: 24,
                color: "#000",
              }
            : {}),
        }}
      >
        <CloseIcon style={{ color: "black" }} />
      </IconButton>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          backgroundColor: "transparent",
          padding: isMobile ? "0" : "20px",
          position: "relative", // For positioning the download button
        }}
      >
        <img
          src={imageUrl || ""}
          style={{
            maxWidth: isMobile ? "100%" : "80vw",
            maxHeight: isMobile ? "100%" : "80vh",
            objectFit: "cover",
            borderRadius: "8px",
          }}
        />
        <IconButton
          aria-label="download"
          sx={{
            position: "absolute",
            bottom: 30,
            right: 30,
            backgroundColor: "#e5e5e5",
            padding: "5px",
            "&:hover": {
              backgroundColor: "rgb(229, 229, 229, 0.6)",
            },
          }}
          onClick={async (e) => {
            e.preventDefault();
            window.open(imageUrl || "", "_blank");
          }}
        >
          <Download />
        </IconButton>
      </div>
    </Dialog>
  );
};

export default ({
  file,
  removeUpload,
  canOpen = false,
}: {
  file: FileUpload;
  removeUpload: (filename: string) => void;
  canOpen?: boolean;
}) => {
  const isMobile = useIsMobile();
  let isImage = file?.file?.type.startsWith("image/");
  let imageUrl = file?.file ? URL.createObjectURL(file?.file) : "";
  const [showRemoveButton, setShowRemoveButton] = useState(isMobile);
  const [openAttachment, setOpenAttachment] = useState<FileUpload | null>(null);

  const isContactCard = file.contentType.includes("application/x-contact-card");

  if (!isImage && file?.url && file.contentType.startsWith("image/")) {
    isImage = true;
    imageUrl = file.url;
  }

  return (
    <Box
      sx={{
        display: "flex",
        position: "relative",
        alignItems: "center",
        borderRadius: "8px",
        border: "1px solid #D4D4D4",
      }}
      onMouseEnter={() => setShowRemoveButton(true)}
      onMouseLeave={() => {
        setShowRemoveButton(false);
      }}
    >
      {isImage && (
        <Box
          component="img"
          onClick={() => {
            if (canOpen) {
              setOpenAttachment(file);
            }
          }}
          sx={{
            height: "64px",
            width: "64px",
            objectFit: "cover",
            borderRadius: "6px",
            cursor: canOpen ? "pointer" : "default",
          }}
          src={imageUrl}
        />
      )}

      {!isImage && (
        <Box
          sx={{
            display: "flex",
            gap: "4px",
            alignItems: "center",
            padding: "8px 14px 8px 12px",
          }}
        >
          {isContactCard ? (
            <SquareUserRound size={16} color={theme.palette.text.tertiary} />
          ) : (
            <Paperclip size={16} color={theme.palette.text.tertiary} />
          )}
          <Typography
            variant="bodySmallHeavy"
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            {file.filename}
          </Typography>
        </Box>
      )}

      {/* Show the X button on hover */}
      {showRemoveButton && (
        <IconButton
          sx={{
            position: "absolute",
            top: "-8px",
            right: "-8px",
            alignItems: "start",
            borderRadius: "50px",
            background: "white",
            border: (theme) => `1px solid ${theme.palette.border}`,

            "&:hover": { background: "white" },
          }}
          onClick={() => removeUpload(file.filename)}
        >
          <X
            size={isMobile ? 20 : 16}
            style={{
              margin: "2px",
              filter: "drop-shadow(0px 0px 4px rgba(0, 0, 0, 0.25))",
            }}
          />
        </IconButton>
      )}
      {canOpen && (
        <ImageDialog
          imageUrl={openAttachment ? imageUrl : null}
          onClose={() => setOpenAttachment(null)}
        />
      )}
    </Box>
  );
};
